import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { WorkflowStepInterface } from '@vendasta/automata';
import { Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { UsersService } from '../../../../data-services/users.service';
import { isReferencingAPreviousStep } from '../../common/utils/step-reference';
import {
  ADDITIONAL_SALESPEOPLE,
  ASSIGNED_FULFILLMENT_AGENT,
  ASSIGNED_SALESPERSON,
  NONE_ASSIGNED,
} from '../../../../data-services/users.service';
import { AutomationErrorHandlerService, StringFormattingService } from '../../../services';

/**
 * <h1> IMPORTANT </h1>
 *
 * Do not implement new actions, triggers, and filters using {@link CommonDisplayActionService}
 * nor {@link CommonDisplayActivityService}. These classes are deprecated, retained for backward
 * compatibility only, and <strong> are likely to be removed in the future. </strong>
 * Instead, subclass from {@link AutomationDisplayService} as this implements all modern interfaces,
 * provides convenience methods to make code more concise and readable,
 * and provides adapters to the old system while it is being phased out.
 * @see AutomationDisplayService
 * @example AddTagService
 */
@Injectable({ providedIn: 'root' })
export class SendNotificationDisplayService {
  constructor(
    private readonly translateService: TranslateService,
    private readonly errorHandler: AutomationErrorHandlerService,
    private readonly usersService: UsersService,
    private readonly stringFormatService: StringFormattingService,
  ) {}

  getActivityTitle(userIds: string[]): Observable<string> {
    const userCount = userIds.length;
    let names$ = of('');
    if (userCount > 0) {
      names$ = this.usersService.getUsersById(userIds).pipe(
        take(1),
        map((users) => {
          return users.map((u) => [u.firstName, u.lastName].join(' '));
        }),
        map((userNames) => this.stringFormatService.formatList(userNames, 3)),
      );
    }
    return names$.pipe(
      switchMap((names) => {
        if (userCount > 0 && !!names) {
          return this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.SEND_NOTIFICATIONS.ACTIVITY.TO_NAMES', {
            names: names,
          });
        } else if (userCount === 0) {
          return this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.SEND_NOTIFICATIONS.ACTIVITY.DID_NOT_SEND');
        }
        return this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.SEND_NOTIFICATIONS.ACTIVITY.SENT_A_NOTIFICATION');
      }),
    );
  }

  getTemplateActionSubtitle(step: WorkflowStepInterface, persona: string): Observable<string> {
    const data = step ? JSON.parse(step.data) : {};
    if (data.user_ids?.length === 1) {
      if (persona === 'sales_person' && data.user_ids[0] === ASSIGNED_SALESPERSON) {
        return this.translateService.stream(
          'AUTOMATIONS.EDITOR.TASKS.SEND_NOTIFICATIONS.ACTIONS.TO_ASSIGNED_SALESPERSON',
        );
      }
      if (persona === 'fulfillment_agent' && data.user_ids[0] === ASSIGNED_FULFILLMENT_AGENT) {
        return this.translateService.stream(
          'AUTOMATIONS.EDITOR.TASKS.SEND_NOTIFICATIONS.ACTIONS.TO_ASSIGNED_FULFILLMENT_AGENT',
        );
      }
    }
    if (persona === 'partner_admin') {
      return this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.SEND_NOTIFICATIONS.ACTIONS.TO_A_PARTNER_ADMIN');
    }
    return this.getActionSubtitleEmptyState(step);
  }

  getActionSubtitle(
    step: WorkflowStepInterface,
    multipleUsersString: string,
    assignedUserString: string,
    additionalAssigneesString?: string,
    noneAssignedString?: string,
  ): Observable<string> {
    const data = step ? JSON.parse(step.data) : {};
    if (data.user_ids?.length > 0) {
      if (data.user_ids?.length === 1) {
        if (data.user_ids[0] === ASSIGNED_SALESPERSON || data.user_ids[0] === ASSIGNED_FULFILLMENT_AGENT) {
          return this.translateService.stream(assignedUserString);
        } else if (data.user_ids[0] === ADDITIONAL_SALESPEOPLE) {
          return this.translateService.stream(additionalAssigneesString);
        } else if (isReferencingAPreviousStep(data.user_ids[0])) {
          return this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.SEND_NOTIFICATIONS.ACTIONS.FROM_PREVIOUS_STEP');
        } else if (data.user_ids[0] === NONE_ASSIGNED) {
          this.errorHandler.setValidationError({
            stepId: step?.id,
            taskDefinitionId: step?.taskDefinitionId,
            errorMessage: this.translateService.instant(
              'AUTOMATIONS.EDITOR.TASKS.SEND_NOTIFICATIONS.ACTIONS.CONFIG_ERROR',
            ),
            isNonDismissable: true,
          });
          return this.translateService.stream(noneAssignedString);
        } else {
          return this.usersService.getUsersById(data.user_ids).pipe(
            map((users) => {
              const name = this.usersService.getUserDisplay(users, users[0].userId);
              return this.translateService.instant(
                'AUTOMATIONS.EDITOR.TASKS.SEND_NOTIFICATIONS.ACTIONS.TO_SINGLE_USER',
                {
                  name: name,
                },
              );
            }),
          );
        }
      } else {
        return this.translateService.stream(multipleUsersString, { userCount: `${data.user_ids?.length || 0}` });
      }
    } else {
      this.errorHandler.setValidationError({
        stepId: step?.id,
        taskDefinitionId: step?.taskDefinitionId,
        errorMessage: this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.SEND_NOTIFICATIONS.ACTIONS.CONFIG_ERROR'),
        isNonDismissable: true,
      });
      return this.getActionSubtitleEmptyState(step);
    }
  }

  getActionSubtitleEmptyState(_step: WorkflowStepInterface): Observable<string> {
    return this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.SEND_NOTIFICATIONS.SEND_A_NOTIFICATION');
  }
}
